import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuthStore } from '../stores/authStore'
import { WhatsAppChatWidget } from '../components/chat'
import { useChatService } from '../services/chatService'
import MobileBottomBar from '../components/layout/MobileBottomBar'
import DatabaseTest from '../components/DatabaseTest'

const ChatPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const [whatsappStatus, setWhatsappStatus] = useState({
    registered: false,
    verified: false,
    phoneNumber: undefined as string | undefined,
    referralCode: undefined as string | undefined
  })
  const { checkWhatsAppStatus, getAdminUrl } = useChatService()

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth/login', { 
        state: { returnTo: '/chat' }
      })
    }
  }, [isAuthenticated, navigate])

  // Check WhatsApp status
  useEffect(() => {
    if (user?.id) {
      const checkStatus = async () => {
        try {
          const status = await checkWhatsAppStatus(user.id)
          setWhatsappStatus({
            registered: status.registered,
            verified: status.verified,
            phoneNumber: status.phoneNumber || undefined,
            referralCode: status.referralCode || undefined
          })
        } catch (error) {
          console.error('Failed to check WhatsApp status:', error)
        }
      }
      checkStatus()
    }
  }, [user?.id, checkWhatsAppStatus])

  const handleViewAdminDashboard = () => {
    window.open(getAdminUrl(), '_blank')
  }

  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  return (
    <>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={pageVariants}
        className="min-h-screen bg-gray-50"
      >
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center text-gray-600 hover:text-gray-900"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Home
                </button>
                <div className="h-6 border-l border-gray-300" />
                <h1 className="text-xl font-semibold text-gray-900">Chat Support</h1>
              </div>

              <div className="flex items-center space-x-3">
                {/* WhatsApp Status Badge */}
                {whatsappStatus.registered && (
                  <div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-sm text-green-700 font-medium">
                      WhatsApp Connected
                    </span>
                  </div>
                )}

                {/* Admin Dashboard Button */}
                <button
                  onClick={handleViewAdminDashboard}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Admin Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Chat Instructions */}
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-blue-900 mb-1">AI-Powered Tyre Assistant</h3>
                  <div className="text-sm text-blue-700 space-y-2">
                    <p>Get instant help with:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Tyre recommendations for your vehicle</li>
                      <li>Finding nearby fitment centres</li>
                      <li>Booking appointments</li>
                      <li>Price comparisons and deals</li>
                      <li>Technical tyre questions</li>
                    </ul>
                    {whatsappStatus.referralCode && (
                      <div className="mt-3 p-2 bg-blue-100 rounded border">
                        <p className="font-medium">Your Referral Code: {whatsappStatus.referralCode}</p>
                        <p className="text-xs">Share TyreLink with friends and family — find tyres and mags near you, free on WhatsApp.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Database Test (Development Only) */}
          {import.meta.env.DEV && (
            <div className="mb-6">
              <DatabaseTest />
            </div>
          )}

          {/* Chat Widget */}
          <div className="bg-white rounded-lg shadow-lg">
            <WhatsAppChatWidget 
              userId={user.id}
              isFullscreen={true}
              className="rounded-lg"
            />
          </div>

          {/* Additional Features */}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/search')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center"
                >
                  <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search Tyres
                </button>
                <button
                  onClick={() => navigate('/profile')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center"
                >
                  <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  My Profile
                </button>
                <button
                  onClick={() => navigate('/bookmarks')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center"
                >
                  <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  Saved Items
                </button>
              </div>
            </div>

            {/* Support Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3">Support Features</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Response Time</span>
                  <span className="text-sm font-medium text-green-600">Instant</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">AI Assistant</span>
                  <span className="text-sm font-medium text-blue-600">24/7 Available</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">WhatsApp Integration</span>
                  <span className={`text-sm font-medium ${whatsappStatus.registered ? 'text-green-600' : 'text-gray-400'}`}>
                    {whatsappStatus.registered ? 'Connected' : 'Not Connected'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomBar />
    </>
  )
}

export default ChatPage